"use client";

import { useEffect, useRef } from "react";

/** Il contenitore della testata. Pubblica la sua altezza vera come
 *  `--oi-header-h` sull'`<html>`, a ogni cambio di misura.
 *
 *  La testata **scorre via con la pagina su tutte le rotte**, e non
 *  resta appesa. Fino al 30/09/2026 era `relative` solo su /, /rullo e
 *  /editoriali (un elenco di rotte scritto qui a mano, da tenere allineato
 *  con una regola di globals.css) e `sticky` altrove, alla stessa
 *  altezza: 171,5 pixel a 1440 e 71 sul telefono. Da quando la testata
 *  della prima pagina è quella di tutto il sito il comportamento è uno
 *  solo, quello del suo riferimento, per due ragioni misurate:
 *
 *  - la riga del marchio non ha fondo (è la cornice della prima pagina),
 *    quindi appesa lasciava scorrere il testo sotto il marchio;
 *  - 171 pixel fissi sono un quinto di uno schermo da 900.
 *
 *  I blocchi che restano appesi (la barra del rullo, il margine degli
 *  editoriali) si agganciano quindi al bordo della finestra, non a
 *  `--oi-header-h`. La variabile resta per chi misura *la testata* e non
 *  la usa come appoggio: il pannello del telefono, che si apre sotto di
 *  lei e ne sottrae l'altezza dalla sua.
 *
 *  Un'eccezione sola, chiesta dalla rotta e non scritta qui: il Rullo
 *  mette `data-testata-fissa` sulla sua radice e globals.css appende la
 *  testata in modo che resti in vista la sola fascia delle sezioni (sotto
 *  `lg` la riga dell'hamburger). Per quello qui si pubblica anche
 *  `--oi-testata-fissa-h`, l'altezza della parte che resta: la leggono la
 *  regola e il contenuto della rotta (`--oi-fissa`), che deve lasciarla
 *  libera.
 *
 *  `data-sticky-top-stack` lo legge globals.css per nascondere la
 *  testata sotto un pannello modale. */
export function StickyTopStack({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* La fascia delle sezioni, cioè quello che resta in vista sulle
       rotte con la testata fissa (`data-testata-fissa`, regola in
       globals.css). Sotto `lg` è `display: none` (altezza zero), e allora
       resta in vista tutta la riga dell'hamburger: la testata intera. */
    const fascia = el.querySelector<HTMLElement>("[data-fascia-sezioni]");
    const update = () => {
      const s = document.documentElement.style;
      s.setProperty("--oi-header-h", `${el.offsetHeight}px`);
      const h = fascia && fascia.offsetHeight > 0 ? fascia.offsetHeight : el.offsetHeight;
      s.setProperty("--oi-testata-fissa-h", `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (fascia) ro.observe(fascia);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--oi-header-h");
      document.documentElement.style.removeProperty("--oi-testata-fissa-h");
    };
  }, []);

  return (
    <div ref={ref} data-sticky-top-stack className="relative z-20 no-print">
      {children}
    </div>
  );
}
